
import React, { useState, useEffect, useRef } from 'react';
import { Puzzle, AppSettings } from '../types';
import { ArrowLeft, RefreshCw, Eye, Lock } from 'lucide-react';
import { playSnap, triggerHaptic } from '../utils/feedback';

interface PuzzleBoardProps {
  puzzle: Puzzle;
  settings: AppSettings;
  onComplete: (moves: number, time: number) => void;
  onBack: () => void;
  isChallenge?: boolean;
  isFullscreen?: boolean;
}

const GRID_COLS = 5;
const GRID_ROWS = 7;
const TOTAL_TILES = GRID_COLS * GRID_ROWS;

const PuzzleBoard: React.FC<PuzzleBoardProps> = ({ puzzle, settings, onComplete, onBack, isChallenge, isFullscreen = false }) => {
  const [tiles, setTiles] = useState<number[]>([]);
  const [selectedTile, setSelectedTile] = useState<number | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [moves, setMoves] = useState(0);
  const startTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    const initial = Array.from({ length: TOTAL_TILES }, (_, i) => i);
    shuffleTiles(initial);
    startTimeRef.current = Date.now();
  }, []);

  const shuffleTiles = (arr: number[]) => {
    let shuffled = [...arr];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setTiles(shuffled);
    setMoves(0);
    setSelectedTile(null);
  };

  const handleTileClick = (index: number) => {
    const tileValue = tiles[index];
    if (tileValue === index) return;

    if (selectedTile === null) {
      setSelectedTile(index);
      triggerHaptic(settings, 'light');
    } else {
      if (selectedTile !== index) {
        const newTiles = [...tiles];
        const valA = newTiles[selectedTile];
        const valB = newTiles[index];

        [newTiles[selectedTile], newTiles[index]] = [valB, valA];
        
        if (newTiles[selectedTile] === selectedTile || newTiles[index] === index) {
          playSnap(settings);
          triggerHaptic(settings, 'medium');
        } else {
          triggerHaptic(settings, 'light');
        }

        setTiles(newTiles);
        setMoves(m => m + 1);
        setSelectedTile(null);

        if (newTiles.every((t, i) => t === i)) {
          triggerHaptic(settings, 'success');
          const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
          setTimeout(() => onComplete(moves + 1, elapsed), 1200);
        }
      } else {
        setSelectedTile(null);
      }
    }
  };

  return (
    <div className={`flex-1 flex flex-col items-center bg-gray-950 text-white overflow-hidden ${isFullscreen ? 'fixed inset-0 z-[190] w-screen h-screen justify-center' : 'h-full w-full'}`}>
      {/* Fixed Header */}
      {!isFullscreen && (
        <header className="w-full flex justify-between items-center p-4 pt-safe bg-gray-900/90 backdrop-blur-xl z-30 border-b border-white/5 px-safe shrink-0">
          <button onClick={onBack} className="flex items-center gap-2 hover:text-blue-400 transition text-gray-400 font-bold px-2 py-1">
            <ArrowLeft className="w-5 h-4" /> <span className="hidden sm:inline text-sm">Exit</span>
          </button>
          <div className="text-center">
            <h2 className={`text-[10px] font-cinzel uppercase tracking-[0.2em] leading-none mb-1 ${isChallenge ? 'text-rose-400' : 'text-blue-400'}`}>
              {isChallenge ? 'FAITH DUEL' : puzzle.title}
            </h2>
            <p className="text-[9px] text-gray-500 uppercase tracking-widest">{moves} Moves • 35 Pieces</p>
          </div>
          <div className="flex gap-1.5">
            <button 
              onMouseDown={() => setShowPreview(true)} 
              onMouseUp={() => setShowPreview(false)} 
              onTouchStart={() => setShowPreview(true)} 
              onTouchEnd={() => setShowPreview(false)} 
              className="p-2 bg-gray-800 rounded-lg border border-gray-700 active:scale-95 transition-transform"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button 
              onClick={() => shuffleTiles(Array.from({ length: TOTAL_TILES }, (_, i) => i))} 
              className="p-2 bg-gray-800 rounded-lg border border-gray-700 active:scale-95 transition-transform"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </header>
      )}

      {/* Main Board Area */}
      <main className="flex-1 w-full flex items-center justify-center p-6 relative">
        <div 
          className="relative bg-gray-900 overflow-hidden shadow-2xl flex flex-wrap content-start rounded-2xl ring-2 ring-white/10"
          style={{ 
            /* 
               The 'min' function ensures the board fits the smaller of the two screen dimensions.
               Calculated for a 5x7 grid:
               - Width = board width
               - Height = board width * 1.4 (7/5)
            */
            width: isFullscreen 
              ? 'min(calc(100vw - 24px), calc((100vh - 32px) * 5 / 7))' 
              : 'min(calc(100vw - 48px), calc((100vh - 180px) * 5 / 7))',
            aspectRatio: '5 / 7'
          }}
        >
          {!showPreview ? (
            tiles.map((tileIndex, i) => {
              const originalRow = Math.floor(tileIndex / GRID_COLS);
              const originalCol = tileIndex % GRID_COLS;
              const isSelected = selectedTile === i;
              const isCorrect = tileIndex === i;

              return (
                <div 
                  key={i}
                  onClick={() => handleTileClick(i)}
                  className={`relative transition-all duration-300
                    ${isSelected ? 'z-20 brightness-125 scale-[0.97] ring-4 ring-blue-500 ring-inset shadow-2xl' : ''}
                    ${isCorrect ? 'cursor-default ring-1 ring-white/5 brightness-105' : 'cursor-pointer hover:brightness-110 active:scale-95'}
                  `}
                  style={{
                    width: '20%',
                    height: '14.2857%', /* 100/7 */
                    backgroundImage: `url(${puzzle.imageUrl})`,
                    backgroundSize: '500% 700%', 
                    backgroundPosition: `${(originalCol / (GRID_COLS - 1)) * 100}% ${(originalRow / (GRID_ROWS - 1)) * 100}%`,
                    backgroundRepeat: 'no-repeat',
                  }}
                >
                  {isCorrect && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/10 pointer-events-none">
                      <div className="bg-blue-500/10 p-1 rounded-full border border-white/5">
                        <Lock className="w-2.5 h-2.5 text-white/20" />
                      </div>
                    </div>
                  )}
                  {isSelected && <div className="absolute inset-0 bg-blue-500/20 animate-pulse pointer-events-none" />}
                </div>
              );
            })
          ) : (
            <div className="absolute inset-0 z-10 animate-in fade-in duration-200">
              <img src={puzzle.imageUrl} alt="Preview" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <span className="text-white bg-blue-600/90 backdrop-blur-lg px-8 py-3 rounded-full font-bold uppercase tracking-[0.3em] text-[10px] border border-white/20 shadow-2xl">Reference Revelation</span>
              </div>
            </div>
          )}
        </div>
      </main>
      
      {!isFullscreen && <footer className="pb-safe w-full bg-gray-950/50 h-6 shrink-0" />}
    </div>
  );
};

export default PuzzleBoard;
